import React, { useEffect, useRef } from 'react';
import * as d3 from 'd3';

// Simulated node states that an agent can move through
const AGENT_STATES = ['Online', 'Warning', 'Offline'];

// Random hostname-like IDs for simulated agents
const PREFIXES = ['alpha', 'bravo', 'charlie', 'delta', 'echo', 'foxtrot', 'golf',
  'hotel', 'india', 'juliet', 'kilo', 'lima', 'mike', 'november', 'oscar', 'papa',
  'quebec', 'romeo', 'sierra', 'tango', 'uniform', 'victor', 'whiskey', 'xray'];

let uidCounter = 0;
const makeAgent = (width, height) => {
  const id = `agent_${PREFIXES[uidCounter % PREFIXES.length]}_${Math.floor(Math.random() * 9000) + 1000}`;
  uidCounter++;
  return {
    id,
    group: 'agent',
    radius: 28,
    status: 'Online',
    // Start at a random edge position so they appear to join from outside
    x: Math.random() < 0.5 ? (Math.random() < 0.5 ? -60 : width + 60) : Math.random() * width,
    y: Math.random() < 0.5 ? (Math.random() < 0.5 ? -60 : height + 60) : Math.random() * height,
    // How many ticks before this node's state next changes
    ticksUntilChange: 30 + Math.floor(Math.random() * 60),
    // How many ticks this node has been in Offline state (used for removal)
    offlineTicks: 0,
  };
};

export default function BackgroundGraph() {
  const svgRef = useRef(null);

  useEffect(() => {
    let isMounted = true;
    let interval;

    const width  = window.innerWidth;
    const height = window.innerHeight;

    const svg = d3.select(svgRef.current)
      .attr('width', width)
      .attr('height', height);

    document.body.style.setProperty('background-color', 'transparent', 'important');
    const rootEl = document.getElementById('root');
    if (rootEl) rootEl.style.setProperty('background-color', 'transparent', 'important');

    svg.append('defs').append('style').text(`
      @keyframes pulse-blue {
        0%   { stroke-width: 2px;  stroke-opacity: 0.4; }
        50%  { stroke-width: 12px; stroke-opacity: 0.8; }
        100% { stroke-width: 2px;  stroke-opacity: 0.4; }
      }
      .ir-node { animation: pulse-blue 4s infinite; stroke: rgba(13, 110, 253, 0.8); }

      @keyframes pulse-red {
        0%   { stroke-width: 1px; stroke-opacity: 0.3; }
        50%  { stroke-width: 4px; stroke-opacity: 0.8; }
        100% { stroke-width: 1px; stroke-opacity: 0.3; }
      }
      .glowing-link { stroke: #dc3545; animation: pulse-red 3s infinite; }

      @keyframes pulse-yellow {
        0%   { stroke-width: 1px; stroke-opacity: 0.3; }
        50%  { stroke-width: 4px; stroke-opacity: 0.8; }
        100% { stroke-width: 1px; stroke-opacity: 0.3; }
      }
      .warning-link { stroke: #ffc107; animation: pulse-yellow 3s infinite; }
      .normal-link  { stroke: rgba(255, 255, 255, 0.15); stroke-width: 1.5px; }
    `);

    const linkGroup = svg.append('g');
    const nodeGroup = svg.append('g');

    // Build initial set: 30 ambient particles + 6 starting agents
    const ambientNodes = Array.from({ length: 30 }, (_, i) => ({
      id: `ambient_${i}`,
      group: 'ambient',
      radius: Math.random() * 10 + 5,
    }));

    const irServer = {
      id: 'IR_Server',
      group: 'server',
      radius: 80,
      fx: width / 2,
      fy: height / 2,
    };

    let agentNodes = Array.from({ length: 6 }, () => makeAgent(width, height));

    let nodesData = [irServer, ...ambientNodes, ...agentNodes];

    const simulation = d3.forceSimulation(nodesData)
      .force('charge',    d3.forceManyBody().strength(d => d.group === 'ambient' ? -80 : -600))
      .force('center',    d3.forceCenter(width / 2, height / 2))
      .force('collision', d3.forceCollide().radius(d => d.radius + 50))
      .force('link',      d3.forceLink([]).id(d => d.id).distance(280))
      .on('tick', () => {
        if (!isMounted) return;
        nodeGroup.selectAll('circle')
          .attr('cx', d => { d.x = Math.max(d.radius, Math.min(width - d.radius, d.x)); return d.x; })
          .attr('cy', d => { d.y = Math.max(d.radius, Math.min(height - d.radius, d.y)); return d.y; });
        linkGroup.selectAll('line')
          .attr('x1', d => d.source.x).attr('y1', d => d.source.y)
          .attr('x2', d => d.target.x).attr('y2', d => d.target.y);
      });

    const nodeColor = d => {
      if (d.group === 'server')  return '#0d6efd';
      if (d.group === 'ambient') return 'rgba(255,255,255,0.1)';
      if (d.status === 'Online')  return '#198754';
      if (d.status === 'Warning') return '#ffc107';
      return '#dc3545';
    };
    const nodeStroke = d =>
      (d.group === 'server' || d.group === 'ambient') ? null
        : d.status === 'Offline' ? '#dc3545' : 'rgba(255,255,255,0.5)';
    const nodeStrokeW = d =>
      (d.group === 'server' || d.group === 'ambient') ? null : 2;

    const redraw = (isFirst = false) => {
      const linksData = agentNodes.map(a => ({
        source: 'IR_Server',
        target: a.id,
        status: a.status,
      }));

      // Separate enter/update paths so the opacity fade-in on new nodes is
      // never interrupted by a second transition on the merged selection.
      nodeGroup.selectAll('circle')
        .data(nodesData, d => d.id)
        .join(
          enter => enter.append('circle')
            .attr('r', d => d.radius)
            .attr('class', d => d.group === 'server' ? 'ir-node' : '')
            .attr('cx', d => d.x || width / 2)
            .attr('cy', d => d.y || height / 2)
            .attr('fill', nodeColor)
            .attr('stroke', nodeStroke)
            .attr('stroke-width', nodeStrokeW)
            .attr('opacity', 0)
            .call(sel => sel.transition().duration(2000).ease(d3.easeLinear).attr('opacity', 1)),
          update => update
            .transition().duration(2000).ease(d3.easeLinear)
            .attr('fill', nodeColor)
            .attr('stroke', nodeStroke)
            .attr('stroke-width', nodeStrokeW),
          exit => exit.transition().duration(600).ease(d3.easeLinear).attr('opacity', 0).remove()
        );

      linkGroup.selectAll('line')
        .data(linksData, d => `${d.source.id || d.source}-${d.target.id || d.target}`)
        .join('line')
        .attr('class', d => {
          if (d.status === 'Offline') return 'glowing-link';
          if (d.status === 'Warning') return 'warning-link';
          return 'normal-link';
        });

      simulation.nodes(nodesData);
      simulation.force('link').links(linksData).distance(d => {
        if (d.status === 'Online')  return 280;
        if (d.status === 'Warning') return 420;
        return 600;
      });
      simulation.alpha(isFirst ? 1 : 0.3).restart();
    };

    redraw(true);

    // Tick: evolve agent states and add/remove nodes every 3 s.
    const tick = () => {
      if (!isMounted) return;

      agentNodes = agentNodes
        .map(a => {
          const next = { ...a, ticksUntilChange: a.ticksUntilChange - 1 };
          if (next.ticksUntilChange <= 0) {
            const currentIdx = AGENT_STATES.indexOf(a.status);
            next.status = AGENT_STATES[(currentIdx + 1) % AGENT_STATES.length];
            next.ticksUntilChange = 20 + Math.floor(Math.random() * 50);
            next.offlineTicks = 0;
          }
          if (next.status === 'Offline') {
            next.offlineTicks = (next.offlineTicks || 0) + 1;
          }
          return next;
        })
        // Remove nodes that have been Offline for ≥3 ticks (~9 s)
        .filter(a => !(a.status === 'Offline' && a.offlineTicks >= 3));

      // Randomly add a new agent (≤8 total) or remove one if too many
      const roll = Math.random();
      if (agentNodes.length < 8 && roll < 0.4) {
        agentNodes.push(makeAgent(width, height));
      } else if (agentNodes.length > 3 && roll > 0.85) {
        const offlineIdx = agentNodes.findIndex(a => a.status !== 'Online');
        agentNodes.splice(offlineIdx >= 0 ? offlineIdx : Math.floor(Math.random() * agentNodes.length), 1);
      }

      nodesData = [irServer, ...ambientNodes, ...agentNodes];
      redraw();
    };

    interval = setInterval(tick, 3000);

    const handleResize = () => {
      const w = window.innerWidth, h = window.innerHeight;
      svg.attr('width', w).attr('height', h);
      irServer.fx = w / 2;
      irServer.fy = h / 2;
      simulation.force('center', d3.forceCenter(w / 2, h / 2)).restart();
    };

    const handleVisibility = () => {
      if (document.hidden) {
        clearInterval(interval);
        simulation.stop();
      } else {
        simulation.alpha(0.3).restart();
        interval = setInterval(tick, 3000);
      }
    };

    window.addEventListener('resize', handleResize);
    document.addEventListener('visibilitychange', handleVisibility);

    return () => {
      isMounted = false;
      clearInterval(interval);
      simulation.stop();
      window.removeEventListener('resize', handleResize);
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, []);

  return (
    <svg
      ref={svgRef}
      style={{
        position: 'fixed', top: 0, left: 0,
        width: '100vw', height: '100vh',
        zIndex: -1, backgroundColor: 'transparent',
        pointerEvents: 'none',
      }}
    />
  );
}
